/* eslint-disable react/no-danger */
import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { useFetch } from 'hooks';
import { errorHandler } from 'utils';
import { formattedDateTimezone } from 'utils/formatter';

import { usePhoneConfirmationOpen } from 'containers/common/PhoneConfirmation';

// Components
import { Mui } from 'components';
import { DisplayData } from 'components/common';
import { Stack } from 'components/layouts';

// Local Components
import * as api from './api';

const useStyles = Mui.makeStyles(() => ({
  clickableRow: { cursor: 'pointer' },
  lastBorder: { '& tr:last-child td': { borderBottom: 0 } },
  policyContent: { '& ul ul li': { marginLeft: '20px !important' } },
}));

function EskolarePolicyAcceptancePopup() {
  // Hooks
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const phoneConfirmationOpen = usePhoneConfirmationOpen();

  // States
  const [accepting, setAccepting] = useState(false);

  // Fetch
  const { data, error, mutate } = useFetch([api.endpoints.list()]);
  const termsOfUse =
    data?.find?.(
      policy =>
        policy.tags?.includes('terms-of-use') && !!policy.content && !policy.content?.isAccepted,
    ) || null;
  const privacyPolicy =
    data?.find?.(
      policy =>
        policy.tags?.includes('privacy-policy') && !!policy.content && !policy.content?.isAccepted,
    ) || null;

  // Actions
  const handleAcceptPolicies = () => {
    setAccepting(true);
    const policies = [];
    if (termsOfUse) policies.push(termsOfUse.uid);
    if (privacyPolicy) policies.push(privacyPolicy.uid);
    return api
      .accept({ policies })
      .then(() => {
        mutate();
        enqueueSnackbar('Política(s) aceita(s) com sucesso!', { variant: 'success' });
      })
      .catch(err =>
        enqueueSnackbar(errorHandler(err.response?.data) || 'Erro no aceite da(s) política(s)', {
          variant: 'error',
        }),
      )
      .finally(() => setAccepting(false));
  };

  if ((!data && !error) || (!termsOfUse && !privacyPolicy)) return null;

  const renderPolicyBlock = (policy, title) => (
    <Stack>
      <Mui.Typography variant="h4">{title}</Mui.Typography>
      <Mui.Typography variant="body1">
        Atualizado em{' '}
        <DisplayData.DateField value={formattedDateTimezone(policy.content?.datePublished)} />
      </Mui.Typography>
      <div
        className={classes.policyContent}
        dangerouslySetInnerHTML={{ __html: policy.content?.html }}
      />
      <br />
    </Stack>
  );

  return (
    <Mui.Dialog open={(!!termsOfUse || !!privacyPolicy) && !phoneConfirmationOpen} maxWidth="lg">
      <Mui.DialogTitle>
        <Mui.Typography variant="h4">
          {!!termsOfUse && 'Termos de uso'}
          {!!termsOfUse && !!privacyPolicy && ' e '}
          {!!privacyPolicy && 'Política de privacidade'}
        </Mui.Typography>
      </Mui.DialogTitle>
      <Mui.DialogContent>
        <Stack>
          <Mui.Card variant="outlined" overflow="auto" maxHeight={400}>
            <Mui.Box overflow="auto" maxHeight={400}>
              <Mui.CardContent>
                <Stack>
                  {!!termsOfUse && renderPolicyBlock(termsOfUse, 'Termos de uso')}
                  {!!privacyPolicy && renderPolicyBlock(privacyPolicy, 'Política de privacidade')}
                </Stack>
              </Mui.CardContent>
            </Mui.Box>
          </Mui.Card>

          <Mui.Typography variant="body1">
            Ao continuar navegando, você concorda com
            {!!termsOfUse && ' os termos de uso'}
            {!!termsOfUse && !!privacyPolicy && ' e'}
            {!!privacyPolicy && ' a política de privacidade'} da Eskolare.
          </Mui.Typography>
        </Stack>
      </Mui.DialogContent>
      <Mui.DialogActions>
        <Mui.Button
          size="large"
          loading={accepting}
          disabled={accepting}
          onClick={handleAcceptPolicies}>
          Aceitar e Continuar
        </Mui.Button>
      </Mui.DialogActions>
    </Mui.Dialog>
  );
}

export default EskolarePolicyAcceptancePopup;
